import { useCallback } from 'react';
import { useQuery } from "@tanstack/react-query";

import Log from '@/Logger';
import { getDeviceDisplayNames } from '@/feature/api-client';

/**
 * DynamoDBに登録されているデバイスの表示名を取得し、整形する関数。
 * @returns {Promise<DeviceDisplayName>} - `tenantId` と `serialIdAndDisplayNameList` を含むオブジェクト
 * @throws {Error} - APIリクエストが失敗した場合
 */
export async function fetchDeviceDisplayName(): Promise<Map<string, string>> {
    const logger = Log.getLogger();
    // データを取得する
    logger.api.info('Lantana-API', 'Start to request: geDeviceDisplayNames');
    const displayNameList = (await getDeviceDisplayNames()).serialIdAndDisplayNameMappings ?? [];
    logger.api.info('Lantana-API', 'Succeed to request: getDeviceDisplayNames');

    // シリアルIDをキーとして、対応する表示名を取得するためのマッピングを作成
    const newMapping = new Map<string, string>();
    for (const m of displayNameList) {
        if (m.serialId && m.displayName) {
            newMapping.set(m.serialId, m.displayName);
        }
    }
    return newMapping;
}

/**
 * デバイス表示名のマッピングを管理するカスタムフック
 * シリアルIDと表示名のマッピングを取得・キャッシュする
 * useReducerを使用して関連する状態を原子的に管理
 */
export const useDeviceDisplayName = () => {
    const { data: mapping, error, isLoading, isFetching, refetch, } = useQuery({
        queryKey: ['deviceDisplayName'],
        queryFn: fetchDeviceDisplayName,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
    });

    /**
     * シリアルIDに対応する表示名を取得する
     * マッピングが存在しない場合はシリアルIDをそのまま返す
     */
    const getDisplayName = useCallback((serialId: string): string => {
        return mapping?.get(serialId) ?? serialId;
    }, [mapping]);

    return {
        mapping,
        getDisplayName,
        refetch,
        isLoading,
        isFetching,
        error,
    };
};
